'use client';
import * as React from 'react';
import type { RefObject } from '@mui/x-internals/types';
import { useGridSelector, useGridApiMethod, gridDimensionsSelector } from '@mui/x-data-grid';
import type { GridRowId } from '@mui/x-data-grid';
import { gridHorizontalScrollbarHeightSelector, getVisibleRows } from '@mui/x-data-grid/internals';
import type { GridInfiniteLoaderPrivateApi } from '@mui/x-data-grid/internals';
import useEventCallback from '@mui/utils/useEventCallback';
import { styled } from '@mui/material/styles';
import type { GridPrivateApiPro } from '../../../models/gridApiPro';
import type { DataGridProProcessedProps } from '../../../models/dataGridProProps';

const InfiniteLoadingTriggerElement = styled('div', {
  slot: 'internal',
  shouldForwardProp: undefined,
})({
  position: 'sticky',
  // Both 0-insets keep the trigger in the viewport in LTR and RTL alike. Sticky insets
  // are independent constraints, and a width: 0 box satisfies both at once, so it clamps
  // at whichever edge the scroll pushes it past. Without `right`, the trigger scrolls out
  // through the right edge in RTL and the observer never fires again.
  left: 0,
  right: 0,
  width: 0,
  height: 0,
});

interface InfiniteLoadingTriggerProps {
  setTarget: (node: HTMLElement | null) => void;
}

function InfiniteLoadingTrigger(props: InfiniteLoadingTriggerProps) {
  const { setTarget } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  // The connection is scheduled from an effect, not from a ref callback: on mount, Strict Mode in
  // React 18 replays the effects but not the ref callbacks, so a connection scheduled by a ref
  // callback and canceled by an effect cleanup would never be scheduled again.
  React.useEffect(() => {
    // If the user scrolls through the grid too fast it might happen that the observer is connected to the trigger element
    // that will be intersecting the root inside the same render cycle (but not intersecting at the time of the connection).
    // This will cause the observer to not call the callback with `isIntersecting` set to `true`.
    // https://www.w3.org/TR/intersection-observer/#event-loop
    // Delaying the connection to the next cycle helps since the observer will always call the callback the first time it is connected.
    // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/observe
    // Related to
    // https://github.com/mui/mui-x/issues/14116
    const timeout = setTimeout(() => setTarget(ref.current));
    return () => {
      clearTimeout(timeout);
      setTarget(null);
    };
  }, [setTarget]);

  return <InfiniteLoadingTriggerElement ref={ref} role="none" />;
}

/**
 * @requires useGridDimensions (method) - can be after
 */
export const useGridInfiniteLoadingIntersection = (
  apiRef: RefObject<GridPrivateApiPro>,
  props: Pick<
    DataGridProProcessedProps,
    'onRowsScrollEnd' | 'dataSource' | 'lazyLoading' | 'rowsLoadingMode' | 'scrollEndThreshold'
  >,
): void => {
  const observer = React.useRef<IntersectionObserver>(null);
  const triggerElement = React.useRef<HTMLElement | null>(null);
  // The last row when the trigger entered the threshold area
  const intersectingLastRowId = React.useRef<GridRowId | null>(null);

  const isEnabledClientSide = props.rowsLoadingMode === 'client' && !!props.onRowsScrollEnd;
  const isEnabledServerSide = props.dataSource && props.lazyLoading;

  const isEnabled = isEnabledClientSide || isEnabledServerSide;
  const isEnabledAndReady = useGridSelector(
    apiRef,
    () => isEnabled && gridDimensionsSelector(apiRef).isReady,
  );

  // The trigger stays mounted while scrolling, so it stays observed to notify each time it enters
  // the threshold area. It is also remounted without leaving the area (e.g. after the skeleton
  // loading overlay), and a new observation reports the current state. So the event is published
  // only once per last row, until the trigger leaves the area.
  const handleIntersectionChange = useEventCallback((entries: IntersectionObserverEntry[]) => {
    // A removed trigger is reported as not intersecting, but it didn't leave the area
    const connectedEntries = entries.filter((entry) => entry.target.isConnected);
    const entry = connectedEntries[connectedEntries.length - 1];
    if (!entry) {
      return;
    }

    if (!entry.isIntersecting || entry.intersectionRatio !== 1) {
      intersectingLastRowId.current = null;
      return;
    }

    const lastRowId = getVisibleRows(apiRef).rows.at(-1)?.id ?? null;
    if (intersectingLastRowId.current === lastRowId) {
      return;
    }
    intersectingLastRowId.current = lastRowId;
    apiRef.current.publishEvent('rowsScrollEndIntersection');
  });

  React.useEffect(() => {
    const virtualScroller = apiRef.current.virtualScrollerRef.current;
    // Also, check for the availability of the `IntersectionObserver`. It is not available in some test environments, e.g. jsdom
    if (!isEnabledAndReady || !virtualScroller || typeof IntersectionObserver === 'undefined') {
      return;
    }
    observer.current?.disconnect();

    const horizontalScrollbarHeight = gridHorizontalScrollbarHeightSelector(apiRef);
    const marginBottom = props.scrollEndThreshold - horizontalScrollbarHeight;

    observer.current = new IntersectionObserver(handleIntersectionChange, {
      threshold: 1,
      root: virtualScroller,
      rootMargin: `0px 0px ${marginBottom}px 0px`,
    });
    if (triggerElement.current) {
      observer.current.observe(triggerElement.current);
    }
  }, [apiRef, handleIntersectionChange, isEnabledAndReady, props.scrollEndThreshold]);

  const updateTarget = useEventCallback((node: HTMLElement | null) => {
    if (triggerElement.current !== node) {
      observer.current?.disconnect();

      triggerElement.current = node;
      if (triggerElement.current) {
        observer.current?.observe(triggerElement.current);
      }
    }
  });

  const getInfiniteLoadingTriggerElement = React.useCallback<
    NonNullable<GridInfiniteLoaderPrivateApi['getInfiniteLoadingTriggerElement']>
  >(
    ({ lastRowId }) => {
      if (!isEnabled) {
        return null;
      }
      return (
        <InfiniteLoadingTrigger
          // Force rerender on last row change to start observing the new trigger
          key={`trigger-${lastRowId}`}
          setTarget={updateTarget}
        />
      );
    },
    [isEnabled, updateTarget],
  );

  const infiniteLoaderPrivateApi: GridInfiniteLoaderPrivateApi = {
    getInfiniteLoadingTriggerElement,
  };

  useGridApiMethod(apiRef, infiniteLoaderPrivateApi, 'private');
};
